import { useEffect, useState } from "react";
import { RefreshCw, Sparkles } from "lucide-react";
import { fetchInquiries, fetchInquiryStats } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

export default function AdminInquiries() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0 });
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([fetchInquiries(), fetchInquiryStats()])
      .then(([rows, s]) => { setItems(rows); setStats(s); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <section className="container-edge py-20">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10 pb-8 border-b border-ink/10">
        <div>
          <div className="eyebrow mb-3"><Sparkles size={14} className="fill-coral" /> Operations · Inquiries</div>
          <h1 className="display-serif text-4xl sm:text-5xl leading-tight">Quote Requests</h1>
        </div>
        <div className="flex items-center gap-6">
          <Stat label="Total" value={stats.total} testId={ADMIN.statsTotal} />
          <Stat label="New" value={stats.new} testId={ADMIN.statsNew} accent />
          <button onClick={load} className="btn-secondary text-sm" data-testid={ADMIN.refresh}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <div data-testid={ADMIN.list} className="bg-white rounded-3xl ring-1 ring-ink/8 overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-ink/10 font-mono text-[11px] uppercase tracking-[0.2em] text-ink/55">
          <div className="col-span-3">Submitted</div>
          <div className="col-span-3">Contact</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-2">Organization</div>
          <div className="col-span-1">Group</div>
          <div className="col-span-1">Window</div>
        </div>

        {loading && (
          <div className="px-6 py-12 text-center font-mono text-xs uppercase tracking-[0.2em] text-ink/50">Loading…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="px-6 py-16 text-center font-mono text-xs uppercase tracking-[0.2em] text-ink/50">No inquiries yet.</div>
        )}
        {!loading && items.map((it) => (
          <details key={it.id} className="border-b border-ink/10 last:border-b-0">
            <summary className="grid grid-cols-12 gap-4 px-6 py-5 cursor-pointer hover:bg-cream/60 transition-colors list-none">
              <div className="col-span-3">
                <div className="text-sm font-medium">{new Date(it.created_at).toLocaleDateString()}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/45 mt-0.5">{new Date(it.created_at).toLocaleTimeString()}</div>
              </div>
              <div className="col-span-3">
                <div className="text-sm font-medium">{it.name}</div>
                <div className="text-xs text-ink/60 mt-0.5">{it.email}</div>
              </div>
              <div className="col-span-2 text-sm">
                {it.trip_type && <span className="chip bg-coral/15 text-coralDeep">{it.trip_type}</span>}
              </div>
              <div className="col-span-2 text-sm text-ink/80 truncate">{it.organization || "—"}</div>
              <div className="col-span-1 font-mono text-sm">{it.group_size}</div>
              <div className="col-span-1 text-sm text-ink/70 truncate">{it.travel_window || "—"}</div>
            </summary>
            <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-2 gap-6 bg-cream/40">
              <Pair k="Role" v={it.role} />
              <Pair k="Phone" v={it.phone} />
              <Pair k="Tour interest" v={it.tour_slug} />
              <Pair k="Destination" v={it.destination_interest} />
              <Pair k="Duration" v={it.duration_days ? `${it.duration_days} days` : null} />
              <Pair k="Budget" v={it.budget_range} />
              <div className="md:col-span-2">
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink/55 mb-2">Message</div>
                <div className="text-sm leading-relaxed text-ink/85 whitespace-pre-wrap">{it.message || "—"}</div>
              </div>
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}

function Stat({ label, value, testId, accent }) {
  return (
    <div>
      <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink/55">{label}</div>
      <div data-testid={testId} className={`display-serif text-3xl ${accent ? "text-coral" : "text-ink"}`}>{value}</div>
    </div>
  );
}

function Pair({ k, v }) {
  return (
    <div>
      <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink/55 mb-1">{k}</div>
      <div className="text-sm text-ink/85">{v || "—"}</div>
    </div>
  );
}
